import { useEffect, useState } from "react";
import { RefreshCcw } from "lucide-react";

// Detects when a new service-worker / bundle has been deployed and
// nudges the user to refresh. Solves the "PWA caches old JS forever
// until manual reload" problem during active testing without yanking
// users mid-game.
//
// How it works:
//   1. Hook onto the active service worker registration.
//   2. Poll the SW server every CHECK_INTERVAL_MS for a new version
//      (registration.update() — a no-op fetch when nothing changed).
//   3. Listen for the "updatefound" event. When the new SW finishes
//      installing AND the page is still controlled by an OLD SW
//      (controller is truthy), surface the prompt.
//   4. User taps the floating "Update" button → window.location
//      .reload(). The fresh JS bundle (Vite content-hashed) loads.
//
// Why not auto-reload on controllerchange? Reloading silently during
// gameplay would yank the user out of their game. Our SW already
// calls skipWaiting + clients.claim, so the new SW is active the
// moment it installs — we just don't force the page to refresh. The
// user picks the moment.
//
// CHECK_INTERVAL_MS picked at 5 min to balance "users get updates
// fast during a testing session" vs "don't hammer the network."

const CHECK_INTERVAL_MS = 5 * 60_000;

export function UpdatePrompt() {
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    let timer: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    // navigator.serviceWorker.ready resolves once a SW is active.
    // On first PWA install this can take a few seconds; on
    // subsequent loads it resolves immediately.
    navigator.serviceWorker.ready
      .then((reg) => {
        if (cancelled) return;

        // Helper to watch a specific worker as it installs and
        // flip the prompt when it's ready to take over.
        const watch = (worker: ServiceWorker) => {
          worker.addEventListener("statechange", () => {
            if (
              worker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // A new SW finished installing AND the page is
              // currently controlled by a different SW — meaning
              // the bundle on disk is newer than what's running.
              setUpdateReady(true);
            }
          });
        };

        // Catch a SW that's ALREADY installing when this hook
        // mounts (e.g. user opened the app moments after a deploy).
        if (reg.installing) watch(reg.installing);
        // Or one that's installed and waiting for activation.
        // (We use skipWaiting in sw.js so this is rare, but
        // defensive.)
        if (reg.waiting) setUpdateReady(true);
        // Future installations: fire when the browser detects a
        // new SW file on the server.
        reg.addEventListener("updatefound", () => {
          if (reg.installing) watch(reg.installing);
        });

        // Periodic poll so long-lived sessions catch deploys
        // without waiting for a navigation. Errors swallowed —
        // a flaky check shouldn't break the UI.
        timer = setInterval(() => {
          reg.update().catch(() => {});
        }, CHECK_INTERVAL_MS);
      })
      .catch(() => {
        // SW isn't supported / failed to register. Silently skip;
        // the user just won't get auto-update prompts.
      });

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  if (!updateReady) return null;

  // Activating the update requires telling the WAITING worker to take
  // over — a plain reload does NOT do that (the page just re-runs
  // under the same old SW, the new one stays parked in "waiting", and
  // the pill comes right back). That was the "click/refresh does
  // nothing, pill is stuck" bug. So: postMessage skip-waiting to the
  // waiting worker, let pwa.ts's controllerchange handler reload once
  // it activates, and force a reload after a short grace period as a
  // fallback in case controllerchange never fires.
  const applyUpdate = () => {
    void (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg?.waiting) {
          reg.waiting.postMessage("skip-waiting");
          setTimeout(() => window.location.reload(), 1500);
          return;
        }
      } catch {
        // fall through
      }
      window.location.reload();
    })();
  };

  return (
    <button
      type="button"
      onClick={applyUpdate}
      className={
        "fixed bottom-4 right-4 z-[60] inline-flex items-center gap-2 " +
        "px-3.5 py-2 rounded-full bg-primary text-primary-foreground " +
        "text-xs font-display uppercase tracking-widest shadow-lg " +
        "shadow-primary/30 hover:bg-primary/90 transition-colors"
      }
      style={{
        // Keep above the bottom safe area so it doesn't sit under
        // the home indicator on iPhone PWAs.
        bottom: "max(env(safe-area-inset-bottom), 1rem)",
      }}
      aria-label="Reload to apply update"
    >
      <RefreshCcw size={13} />
      Update available
    </button>
  );
}
